import { Appearance, Inventory } from "game-common/models"
import { Callback } from "game-common/util"
import { DisplayObject, Graphics } from "pixi.js"

import { disposeTextTextures } from "../../text/dispose_text"
import { easeOutBack, easeOutCubic } from "../../intro/logo_choreography"

export type PageId = "intro" | "build"

export interface NavButtonMeta {
    title: string
    // The one button that carries the flow forward is drawn filled and sits on the right.
    primary?: boolean
    // Led by a die that is thrown, and lands on a new face, with every press: for the buttons that
    // roll something up at random.
    die?: boolean
    callback: Callback<void>
}

// What a page is allowed to ask of the modal around it. Declared here (rather than importing
// CharacterBuilder) so the pages and the modal don't import each other in a cycle.
export interface BuilderHost {
    switchPage(pageId: PageId): void
    setNavButtons(buttons: NavButtonMeta[]): void
    submitCharacter(appearance: Appearance, inventory: Inventory): void
    // Redraw the screen: pages only ever change their own state, then ask for a repaint.
    refresh(): void
    close(): void
}

export interface RevealOptions {
    /** How far below its place the piece starts, in px. */
    rise?: number
    ms?: number
    /** Start this much larger and settle into place -- a stamp rather than a slide. */
    scaleFrom?: number
    /** Called every frame of the reveal with eased progress, for a piece that does more than fade. */
    onFrame?: (t: number) => void
    /** Called once, the frame the piece lands. */
    onLand?: () => void
}

interface Reveal extends Required<Omit<RevealOptions, "onFrame" | "onLand">> {
    target: DisplayObject
    delay: number
    baseY: number
    baseScaleX: number
    baseScaleY: number
    landed: boolean
    onFrame?: (t: number) => void
    onLand?: () => void
}

const REVEAL_MS = 380
const REVEAL_RISE = 10

/** One piece's state at `elapsed` ms into the entrance: 0 before its delay, 1 once it has landed. */
export const revealProgress = (elapsed: number, delay: number, ms: number): number =>
    Math.max(0, Math.min(1, (elapsed - delay) / Math.max(1, ms)))

export abstract class BuilderPage extends Graphics {
    host: BuilderHost
    abstract readonly pageId: PageId
    // Shown in the screen's header.
    abstract readonly title: string
    // Optional line of guidance, shown at the right of the header.
    readonly hint?: string

    // The entrance choreography: every piece the page asked to have revealed, and how far into the
    // reveal we are. `undefined` means no entrance is running -- the page was repainted in place
    // (a palette click) and everything simply sits where it was drawn.
    private reveals: Reveal[] = []
    private revealClock?: number

    // Draw the page's body into `this`. The body's box is the area between the header and the nav
    // footer -- see theme.BODY_Y / BODY_H.
    abstract build(): void

    // Frame hook, only called while this page is the one on screen.
    animate(_dtMs: number) {
        //
    }

    tearDown() {
        //
    }

    /**
     * Ask for a piece to arrive with the page: after `delay` ms it rises into place and fades up. Only
     * takes effect when the host starts an entrance (opening the screen, or turning to this page); on
     * an in-place repaint the piece is simply drawn where it belongs.
     */
    reveal(target: DisplayObject, delay: number = 0, options: RevealOptions = {}) {
        this.reveals.push({
            target,
            delay,
            rise: options.rise ?? REVEAL_RISE,
            ms: options.ms ?? REVEAL_MS,
            scaleFrom: options.scaleFrom ?? 1,
            onFrame: options.onFrame,
            onLand: options.onLand,
            baseY: target.y,
            baseScaleX: target.scale.x,
            baseScaleY: target.scale.y,
            landed: false,
        })
    }

    /** The page has just come on screen: play every reveal from the top. */
    startEntrance() {
        this.revealClock = 0
        this.applyReveals()
    }

    /** Land every piece now (an in-place repaint, or the player skipping ahead). */
    finishEntrance() {
        if (this.revealClock === undefined) {
            return
        }
        this.revealClock = Number.POSITIVE_INFINITY
        this.applyReveals()
        this.revealClock = undefined
    }

    get entering(): boolean {
        return this.revealClock !== undefined
    }

    /** Advance the entrance; called by the host every frame, before `animate`. */
    tickEntrance(dtMs: number) {
        if (this.revealClock === undefined) {
            return
        }
        this.revealClock += dtMs
        this.applyReveals()
        if (this.reveals.every(reveal => reveal.landed)) {
            this.revealClock = undefined
        }
    }

    private applyReveals() {
        const elapsed = this.revealClock ?? Number.POSITIVE_INFINITY
        this.reveals.forEach(reveal => {
            const raw = revealProgress(elapsed, reveal.delay, reveal.ms)
            const t = reveal.scaleFrom !== 1 ? easeOutBack(raw, 1.4) : easeOutCubic(raw)
            const { target } = reveal
            target.alpha = Math.min(1, easeOutCubic(raw * 1.4))
            target.y = reveal.baseY + reveal.rise * (1 - t)
            if (reveal.scaleFrom !== 1) {
                const scale = reveal.scaleFrom + (1 - reveal.scaleFrom) * t
                target.scale.set(reveal.baseScaleX * scale, reveal.baseScaleY * scale)
            }
            reveal.onFrame?.(t)
            if (raw >= 1 && !reveal.landed) {
                reveal.landed = true
                reveal.onLand?.()
            }
        })
    }

    // Free the per-instance Text textures of the last draw before rebuilding (they are not GC'd).
    protected wipe() {
        this.reveals = []
        this.revealClock = undefined
        ;[...this.children].forEach(disposeTextTextures)
        this.removeChildren()
    }
}
